import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Ban, RotateCcw, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MemberList } from "@/components/NetworkTree";

export const AdminBranchDialog = ({ id, onClose, onChanged, canAct }) => {
  const [b, setB] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [tree, setTree] = useState(false);

  const load = () => { setB(null); api.get(`/admin/branches/${id}`).then(({ data }) => setB(data)).catch(() => toast.error("Could not load branch")); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (id) { setTree(false); setAction(null); load(); } }, [id]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/branches/${id}/${action}`, { reason });
      toast.success(action === "refund" ? `Refund recorded · ₹${data.commission_reversed} commission reversed` : `Branch ${data.status}`);
      setAction(null); setReason(""); load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };

  const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-1.5 border-b border-[#FEE2E2] text-sm"><span className="text-[#B91C1C]">{k}</span><span className="text-right">{v}</span></div>;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-branch-dialog">
        <DialogHeader><DialogTitle>Branch {b?.branch_code}</DialogTitle></DialogHeader>
        {!b ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Row k="Name" v={b.name} /><Row k="Status" v={<span className={`px-1.5 py-0.5 rounded border text-xs ${STATUS_STYLE[b.status]}`}>{STATUS_LABEL[b.status]}</span>} />
                <Row k="Referral code" v={b.referral_code} />
                <Row k="Owner" v={<Link to={`/admin/users/${b.owner_user_id}`} className="underline" data-testid="admin-branch-owner-link">{b.owner_name} ({b.owner_code})</Link>} />
                <Row k="Original sponsor" v={b.original_sponsor_user_id ? <Link to={`/admin/users/${b.original_sponsor_user_id}`} className="underline" data-testid="admin-branch-sponsor-link">{b.sponsor_name} ({b.sponsor_code})</Link> : "—"} />
                <Row k="Sponsor branch" v={b.original_sponsor_branch_code || "—"} />
              </div>
              <div>
                <Row k="Activation order" v={b.activation_order ? b.activation_order.order_code : "—"} />
                <Row k="Payment status" v={b.activation_order ? `${b.activation_order.payment_status}${b.activation_order.payment_provider ? ` · ${b.activation_order.payment_provider}` : ""}` : "—"} />
                <Row k="Order total" v={b.activation_order ? inr(b.activation_order.total) : "—"} />
                <Row k="Direct / downline" v={`${b.direct_referrals} / ${b.downline_members}`} />
                <Row k="Verified sales" v={inr(b.eligible_sales)} /><Row k="Branch commission" v={inr(b.earned_commission)} />
              </div>
            </div>
            {canAct && (
              <div className="flex flex-wrap gap-2">
                {b.status === "active" && <button onClick={() => setAction("suspend")} className="btn-outline text-xs" data-testid="admin-branch-suspend-btn"><Ban className="w-3.5 h-3.5" /> Suspend</button>}
                {b.status === "suspended" && <button onClick={() => setAction("reactivate")} className="btn-outline text-xs" data-testid="admin-branch-reactivate-btn"><RotateCcw className="w-3.5 h-3.5" /> Reactivate</button>}
                {!b.is_primary && b.activation_order?.payment_status === "paid" && <button onClick={() => setAction("refund")} className="btn-outline text-xs text-red-600" data-testid="admin-branch-refund-btn"><Undo2 className="w-3.5 h-3.5" /> Refund & reverse</button>}
                <button onClick={() => setTree(!tree)} className="btn-outline text-xs" data-testid="admin-branch-tree-btn">{tree ? "Hide" : "View"} branch tree</button>
              </div>
            )}
            {action && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-4 space-y-3" data-testid="admin-branch-confirm">
                <p className="text-sm font-semibold">Confirm {action} for {b.branch_code}? This is logged in the audit trail.{action === "refund" && " The activation order is marked refunded and its commissions are reversed."}</p>
                {action !== "reactivate" && <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" className="input-soft text-sm" data-testid="admin-branch-reason-input" />}
                <div className="flex gap-2">
                  <button disabled={busy} onClick={run} className="btn-primary text-xs" data-testid="admin-branch-confirm-btn">{busy ? "Working…" : "Confirm"}</button>
                  <button onClick={() => setAction(null)} className="btn-outline text-xs" data-testid="admin-branch-cancel-btn">Cancel</button>
                </div>
              </div>
            )}
            {tree && <div className="border border-[#FEE2E2] rounded-xl p-3"><MemberList code={b.referral_code} /></div>}
            <div>
              <h4 className="font-semibold text-sm mb-2">Activation order ledger</h4>
              <Ledger rows={b.activation_ledger} testid="admin-branch-activation-ledger" />
              <h4 className="font-semibold text-sm mb-2 mt-4">Commissions earned through this branch</h4>
              <Ledger rows={b.ledger} testid="admin-branch-ledger" />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Ledger = ({ rows, testid }) => (
  <div className="overflow-x-auto border border-[#FEE2E2] rounded-xl" data-testid={testid}>
    <table className="w-full text-xs">
      <thead className="bg-[#FEF2F2]"><tr>{["Date", "Beneficiary", "Category", "Level", "Amount", "Status"].map((h) => <th key={h} className="text-left px-2 py-1.5">{h}</th>)}</tr></thead>
      <tbody>
        {(!rows || rows.length === 0) && <tr><td colSpan={6} className="text-center py-4 text-[#94A3B8]">No entries.</td></tr>}
        {(rows || []).map((t) => <tr key={t.id} className="border-t border-[#FEE2E2]"><td className="px-2 py-1.5">{new Date(t.created_at).toLocaleString("en-IN")}</td><td className="px-2 py-1.5">{t.user_code}</td><td className="px-2 py-1.5">{t.category}</td><td className="px-2 py-1.5">{t.level ? `L${t.level}` : "—"}</td><td className="px-2 py-1.5 font-numbers">{t.type === "debit" ? "-" : ""}{inr(t.amount)}</td><td className="px-2 py-1.5">{t.status || (t.reversed ? "reversed" : "—")}</td></tr>)}
      </tbody>
    </table>
  </div>
);
